import React, { useState } from "react";
import {
  Accordion,
  AccordionGroup,
  Button,
  Flex,
  LoadingIndicator,
  ModalV2,
  Text,
  Tag,
} from "@jsq/ds/core";
import { MAX_LOGS_LIMIT } from "~/hooks/useDataStudioQueries/constants";
import { useGetOnboardingJobLogs } from "~/hooks/useDataStudioQueries/useDataStudioQueries";
import * as styles from "./styles_vanilla.css";
import { parseLogEntry } from "./utils/logHelpers";
import {
  getStatusDisplay,
  getStatusTagClassName,
  getTaskStatusVariant,
  getLogLevelClassName,
  deriveStatusFromTaskLogs,
} from "./utils/modalHelpers";
import type { OnboardingJob } from "~/hooks/useDataStudioQueries/types";

interface JobLogsModalProps {
  job: OnboardingJob;
  isOpen: boolean;
  onClose: () => void;
}

export const JobLogsModal: React.FC<JobLogsModalProps> = ({ job, isOpen, onClose }) => {
  const { jobId, createdAt, customerName, environment } = job;
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data, loading, error, refetch } = useGetOnboardingJobLogs(jobId, createdAt, {
    limit: MAX_LOGS_LIMIT,
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  const taskLogs = data?.dataStudioGetOnboardingJobLogs?.taskLogs || [];
  const errors = data?.dataStudioGetOnboardingJobLogs?.errors || [];

  const derivedStatus = deriveStatusFromTaskLogs(taskLogs);
  const statusDisplay = getStatusDisplay(derivedStatus);

  const isDagNotExecutedError = (errorMessage: string): boolean => {
    return errorMessage.toLowerCase().includes("has not been executed");
  };

  const dagNotExecutedErrors = errors.filter(isDagNotExecutedError);
  const actualErrors = errors.filter((err) => !isDagNotExecutedError(err));

  return (
    <ModalV2
      show={isOpen}
      onHide={onClose}
      size="xl"
      title={customerName}
      closeButton={true}
      contentClassName={styles.wideModalContent}
    >
      <ModalV2.Body>
        {/* Job Info Header with Refresh Button */}
        <Flex
          alignItems="center"
          justifyContent="space-between"
          className={styles.modalHeaderFlex}
        >
          <Flex alignItems="center" className={styles.tagContainer}>
            <Tag variant="neutral" className={styles.environmentTag}>
              {environment}
            </Tag>
            <Tag variant="primary" className={getStatusTagClassName(statusDisplay.color)}>
              {statusDisplay.text}
            </Tag>
          </Flex>

          {/* Refresh Button */}
          <Button
            variant="neutral"
            onClick={handleRefresh}
            disabled={isRefreshing || loading}
            isLoading={isRefreshing}
            icon="repeat"
          >
            Refresh
          </Button>
        </Flex>

        {/* Warning Display for DAG not executed */}
        {dagNotExecutedErrors.length > 0 && (
          <div className={styles.warningContainer}>
            <Text variant="strong-md" className={styles.warningText}>
              Warnings:
            </Text>
            {dagNotExecutedErrors.map((err, idx) => (
              <Text key={idx} variant="secondary-sm" className={styles.warningText}>
                {err}
              </Text>
            ))}
          </div>
        )}

        {/* Error Display for actual errors */}
        {actualErrors.length > 0 && (
          <div className={styles.errorContainer}>
            <Text variant="strong-md" className={styles.errorText}>
              Errors:
            </Text>
            {actualErrors.map((err, idx) => (
              <Text key={idx} variant="secondary-sm" className={styles.errorText}>
                {err}
              </Text>
            ))}
          </div>
        )}

        {/* Loading State */}
        {loading && !isRefreshing && (
          <Flex justifyContent="center" alignItems="center" className={styles.loadingContainer}>
            <LoadingIndicator />
          </Flex>
        )}

        {/* Error State */}
        {error && (
          <div className={styles.errorContainerCentered}>
            <Text variant="strong-md" className={styles.errorText}>
              Failed to load logs: {error.message}
            </Text>
          </div>
        )}

        {/* Logs Display */}
        {!loading && !error && taskLogs.length === 0 && (
          <div className={styles.noLogsContainer}>
            <Text variant="secondary-md" className={styles.noLogsText}>
              No logs available for this job
            </Text>
          </div>
        )}

        {!loading && !error && taskLogs.length > 0 && (
          <AccordionGroup className={styles.accordionGroup}>
            {taskLogs.map((task, taskIdx) => {
              const statusVariant = getTaskStatusVariant(task.state);

              return (
                <Accordion
                  key={`${task.taskId}-${task.tryNumber}-${taskIdx}`}
                  title={`Task: ${task.taskId}`}
                  subtitle={`Attempt #${task.tryNumber}`}
                  statusText={task.state.toUpperCase()}
                  statusVariant={statusVariant}
                >
                  <div className={styles.logsContainer}>
                    {task.logEntries.map((entry, entryIdx) => {
                      const logParts = parseLogEntry(entry);

                      return (
                        <div key={entryIdx} className={styles.logEntry}>
                          <span className={getLogLevelClassName(logParts.level)}>
                            {logParts.level}
                          </span>
                          <span className={styles.timestamp}>{logParts.timestamp}</span>
                          <span className={styles.message}>{logParts.message}</span>
                        </div>
                      );
                    })}
                  </div>
                </Accordion>
              );
            })}
          </AccordionGroup>
        )}
      </ModalV2.Body>
    </ModalV2>
  );
};
